import { useEffect, useRef, useState } from "react";
import { Button } from "~/components/ui/button";

type StrongAuthInstructions = {
  action: string;
  method: string;
  authenticity_token: string;
  params: Record<string, unknown>;
};

function assertValidInstructions(
  data: any
): asserts data is StrongAuthInstructions {
  if (!data || typeof data !== "object") {
    throw new Error("Virheellinen vastaus palvelimelta");
  }
  if (typeof data.authenticity_token !== "string" || !data.authenticity_token) {
    throw new Error("Turvatunniste puuttuu");
  }
  if (typeof data.method !== "string" || data.method.toLowerCase() !== "post") {
    throw new Error("Virheellinen kirjautumistapa");
  }
  if (
    typeof data.action !== "string" ||
    !data.action.startsWith("/api/v1/auth/")
  ) {
    throw new Error("Virheellinen toiminto");
  }
  if (
    data.action.startsWith("//") ||
    data.action.includes("://") ||
    data.action.includes("\\")
  ) {
    throw new Error("Virheellinen toiminto");
  }
  if (!data.params || typeof data.params !== "object") {
    throw new Error("Virheelliset parametrit");
  }
}

interface StrongAuthLoginProps {
  apiUrl: string;
  userType: "employee" | "customer";
  buttonText?: string;
  className?: string;
}

export function StrongAuthLogin({
  apiUrl,
  userType,
  buttonText = "Vahva tunnistautuminen",
  className,
}: StrongAuthLoginProps) {
  const [instructions, setInstructions] = useState<
    StrongAuthInstructions | { error: string } | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    let ignore = false;
    async function fetchStrongAuthInstructions() {
      try {
        const endpoint =
          userType === "customer"
            ? "/api/v1/customers/strong-auth/login"
            : "/api/v1/employees/strong-auth/login";

        const fetchUrl = new URL(endpoint, apiUrl);
        // Shared callback exchange route
        fetchUrl.searchParams.set("return_to", "/strong-auth/callback");
        // Failure returns to /login so we can show error there.
        fetchUrl.searchParams.set("failure_return_to", "/login");

        const res = await fetch(fetchUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          credentials: "include",
        });

        if (!ignore) {
          if (!res.ok) {
            setInstructions({
              error: "Palvelin ei vastannut pyyntöön oikein.",
            });
            return;
          }
          const data = await res.json();
          assertValidInstructions(data);
          setInstructions(data);
        }
      } catch (err) {
        if (!ignore) {
          setInstructions({
            error: err instanceof Error ? err.message : "Tuntematon virhe",
          });
        }
      }
    }
    fetchStrongAuthInstructions();
    return () => {
      ignore = true;
    };
  }, [apiUrl, userType]);

  if (!instructions) {
    return (
      <div className={className}>
        <Button disabled className="w-full" variant="outline">
          Ladataan...
        </Button>
      </div>
    );
  }

  if ("error" in instructions) {
    return (
      <div className={className}>
        <Button disabled variant="destructive" className="w-full">
          Vahva tunnistautuminen (Ei käytettävissä)
        </Button>
        {/* <p className="mt-1 text-xs text-red-500">{instructions.error}</p> */}
      </div>
    );
  }

  return (
    <div className={className}>
      <Button
        type="button"
        variant="default"
        className="w-full"
        disabled={isSubmitting}
        onClick={() => {
          setIsSubmitting(true);
          formRef.current?.submit();
        }}
      >
        {buttonText}
      </Button>

      <form
        ref={formRef}
        action={new URL(instructions.action, apiUrl).toString()}
        method="post"
        autoComplete="off"
        rel="noopener noreferrer"
        className="hidden"
      >
        <input
          type="hidden"
          name="authenticity_token"
          value={instructions.authenticity_token}
        />
        {Object.entries(instructions.params).map(([key, value]) => (
          <input key={key} type="hidden" name={key} value={String(value)} />
        ))}
        <noscript>
          <Button type="submit">{buttonText}</Button>
        </noscript>
      </form>
    </div>
  );
}
